import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useGameContext } from '../contexts/GameContext';
import { useGameFlow } from '../hooks/useGameFlow';
import { calculateResponsePreview } from '../scoring/calculator';
// Removed hardcoded incidents import - using scenario-specific incidents instead
import IncidentComponent from '../components/IncidentScreen';
import IncidentResultsScreen from '../components/IncidentResultsScreen';
import './IncidentScreen.css';

export const IncidentScreen: React.FC = () => {
  const { gameState, addIncidentResponse, setGamePhase, calculateFinalScore } = useGameContext();
  const { navigateToScoring } = useGameFlow();
  const [currentIncidentIndex, setCurrentIncidentIndex] = useState(0);
  const [timeOutIncidents, setTimeOutIncidents] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [lastResponse, setLastResponse] = useState<{optionId: string, responseTime: number} | null>(null);
  const [lastResponsePreview, setLastResponsePreview] = useState<ReturnType<typeof calculateResponsePreview> | null>(null);
  
  // Use incidents from the selected scenario
  const incidents = gameState.availableIncidents;
  
  // Initialize game phase if needed
  useEffect(() => {
    if (gameState.gamePhase !== 'gameplay') {
      setGamePhase('gameplay');
    }
  }, [gameState.gamePhase, setGamePhase]);

  const currentIncident = incidents[currentIncidentIndex];
  const isLastIncident = currentIncidentIndex === incidents.length - 1;
  
  // Handle case where no incidents are available
  if (!incidents || incidents.length === 0) {
    return (
      <div className="incident-screen no-incidents">
        <h1>No Incidents Available</h1>
        <p>This scenario does not have any configured incidents.</p>
      </div>
    );
  }

  const handleResponse = (optionId: string, responseTime: number) => {
    // Save the response
    const response = {
      incidentId: currentIncident.id,
      selectedOptionId: optionId,
      responseTime,
      timestamp: new Date()
    };
    addIncidentResponse(response);
    
    // Calculate preview with synergies
    const preview = calculateResponsePreview(
      optionId,
      currentIncident.id,
      responseTime,
      gameState.selectedInvestments
    );
    
    // Store response data and show results
    setLastResponse({ optionId, responseTime });
    setLastResponsePreview(preview);
    setShowResults(true);
  };

  const handleTimeOut = () => {
    // Record timeout
    setTimeOutIncidents([...timeOutIncidents, currentIncident.id]);
    const timeoutResponse = {
      incidentId: currentIncident.id,
      selectedOptionId: 'timeout',
      responseTime: currentIncident.timeLimit,
      timestamp: new Date()
    };
    addIncidentResponse(timeoutResponse);
    
    // For timeout, we don't calculate preview since it's a special case
    // Store timeout response and show results
    setLastResponse({ optionId: 'timeout', responseTime: currentIncident.timeLimit });
    setLastResponsePreview(null);
    setShowResults(true);
  };

  const handleContinueFromResults = () => {
    setShowResults(false);
    setLastResponse(null);
    setLastResponsePreview(null);
    
    if (isLastIncident) {
      // Calculate final scores and move to scoring
      setTimeout(() => {
        calculateFinalScore();
        navigateToScoring();
      }, 500);
    } else {
      // Move to next incident
      setCurrentIncidentIndex(currentIncidentIndex + 1);
    }
  };

  const handleOptionHover = (optionId: string) => {
    // Could implement preview functionality here
  };

  const handleOptionLeave = () => {
    // Clear preview
  };

  // Show results screen if we have a response to show
  if (showResults && lastResponse) {
    if (lastResponse.optionId === 'timeout') {
      // Handle timeout case
      const timeoutOption = {
        id: 'timeout',
        label: 'No Response (Timeout)',
        description: 'You failed to respond within the time limit.',
        impact: {
          security: -50,
          reputation: -50,
          financial: -50,
          operational: -50
        },
        timeRequired: currentIncident.timeLimit
      };
      
      return (
        <IncidentResultsScreen
          incident={currentIncident}
          selectedResponse={timeoutOption}
          scoreImpact={timeoutOption.impact}
          isLastIncident={isLastIncident}
          onContinue={handleContinueFromResults}
        />
      );
    } else {
      const selectedOption = currentIncident.responseOptions.find(opt => opt.id === lastResponse.optionId);
      if (selectedOption) {
        // Calculate score impact for this response
        const scoreImpact = selectedOption.impact;
        
        return (
          <IncidentResultsScreen
            incident={currentIncident}
            selectedResponse={selectedOption}
            scoreImpact={scoreImpact}
            isLastIncident={isLastIncident}
            onContinue={handleContinueFromResults}
            investmentImpactDetails={lastResponsePreview?.investmentImpactDetails}
          />
        );
      }
    }
  }

  return (
    <div className="incident-screen">
      <motion.div 
        className="incident-header"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <h1>Risk Management Challenge</h1>
        <div className="progress-info">
          <span>Incident {currentIncidentIndex + 1} of {incidents.length}</span>
          <div className="progress-bar">
            <motion.div 
              className="progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${((currentIncidentIndex + 1) / incidents.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </motion.div>

      <IncidentComponent
        incident={currentIncident}
        onResponse={handleResponse}
        onTimeOut={handleTimeOut}
        onOptionHover={handleOptionHover}
        onOptionLeave={handleOptionLeave}
        highlightedOptionId={null}
      />
    </div>
  );
};